// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import assertNever from "assert-never";
import { intersection, uniq, uniqBy } from "lodash";
import { makeAutoObservable, reaction, when } from "mobx";

import { SystemId } from "~datatypes";

import {
  locationIdsBySearchType,
  Searchable,
  SearchableGroup,
  SearchType,
} from "../core/models/types";
import { CaseloadSearchable } from "./CaseloadSearchable";
import { CaseloadSearchManager } from "./CaseloadSearchManager";
import { Location } from "./Location";
import { Officer } from "./Officer";
import { OpportunitySearchManager } from "./OpportunitySearch/OpportunitySearchManager";
import { PersonSearchManager } from "./PersonSearchManager";
import { SearchManager } from "./SearchManager";
import { JusticeInvolvedPerson } from "./types";
import { WorkflowsStore } from "./WorkflowsStore";

export class SearchStore {
  workflowsStore: WorkflowsStore;

  clientSearchManager: SearchManager;

  residentSearchManager: SearchManager;

  searchTypeOverride?: SearchType;

  /**
   * Local state to keep track of the selected search ids during impersonation mode, since
   * in impersonation mode the user cannot write to firebase
   */
  selectedSearchIdsForImpersonation: string[] | undefined = undefined;

  /**
   * Local state to keep track of the selected search ids for a user with supervised staff,
   * after the default behavior or auto-setting their search ids (to their own + supervised staff) upon login
   */
  selectedSearchIdsForSupervisorsWithStaff: string[] | undefined = undefined;

  // Delegate for the Typesense-backed caseload search. Owns the scoped-key
  // client, the plan builder, and the typeahead lifecycle. Constructed once
  // in this store's constructor.
  caseloadSearchManager: CaseloadSearchManager;

  // Delegate for the Typesense-backed nav-bar person search. Owns its own
  // scoped-key client and multi_search plan. Constructed once in this store's
  // constructor.
  personSearchManager: PersonSearchManager;

  // Delegate for the Typesense-backed homepage opportunity summaries. Owns its
  // own scoped-key client and multi_search plan. Inert until
  // isTypesenseHomepageEnabled is true.
  opportunitySearchManager: OpportunitySearchManager;

  constructor(workflowsStore: WorkflowsStore) {
    this.workflowsStore = workflowsStore;

    makeAutoObservable(this, {}, { autoBind: true });

    this.clientSearchManager = new SearchManager(this, "CLIENT");
    this.residentSearchManager = new SearchManager(this, "RESIDENT");
    this.caseloadSearchManager = new CaseloadSearchManager(this);
    this.personSearchManager = new PersonSearchManager(this);
    this.opportunitySearchManager = new OpportunitySearchManager(this);

    // update the searchTypeOverride on tenant change
    reaction(
      () => [this.workflowsStore.rootStore.currentTenantId],
      () => {
        this.setSearchTypeOverride(undefined);
      },
    );

    // mirror impersonation selected search with firestore
    reaction(
      () => this.workflowsStore.user?.updates?.selectedSearchIds,
      (searchIds?) =>
        (this.selectedSearchIdsForImpersonation = searchIds ?? []),
    );

    // clear saved search when changing tenants, to prevent cross-contamination
    reaction(
      () => [this.workflowsStore.rootStore.currentTenantId],
      () => {
        this.clearSelectedSearchForTenantChange();
      },
    );

    // set and log default caseload to the user's own, when applicable
    when(
      () =>
        !!this.workflowsStore.user && !!this.workflowsStore.activeSystemConfig,
      () => {
        if (
          !this.workflowsStore.user?.info ||
          !this.workflowsStore.user?.updates
        )
          return;

        // This should only happen once per user with a caseload
        // After the first time login they will always have selectedSearchIds, even if it is an empty array
        if (
          !this.workflowsStore.user.updates.selectedSearchIds &&
          this.workflowsStore.user.info.hasCaseload &&
          this.searchType &&
          ["OFFICER", "INCARCERATION_OFFICER"].includes(this.searchType)
        ) {
          const defaultCaseloadIds = [
            this.workflowsStore.user.info.staffExternalId,
          ];
          this.updateSelectedSearch(defaultCaseloadIds);
          this.workflowsStore.rootStore.analyticsStore.trackCaseloadSearch({
            searchCount: defaultCaseloadIds.length,
            isDefault: true,
            searchType: this.searchType,
          });
        }
      },
    );
  }

  get caseloadPersons(): JusticeInvolvedPerson[] {
    return [
      ...this.residentSearchManager.matchingPersons,
      ...this.clientSearchManager.matchingPersons,
    ];
  }

  get caseloadPersonsSorted(): JusticeInvolvedPerson[] {
    return [
      ...this.residentSearchManager.matchingPersonsSorted,
      ...this.clientSearchManager.matchingPersonsSorted,
    ];
  }

  get caseloadPersonsGrouped(): Record<string, JusticeInvolvedPerson[]> {
    return {
      ...this.clientSearchManager.matchingPersonsGrouped,
      ...this.residentSearchManager.matchingPersonsGrouped,
    };
  }

  // This should only ever return undefined if none of the search configs' FV are enabled
  get searchType(): SearchType | undefined {
    if (this.searchTypeOverride) return this.searchTypeOverride;
    const systemConfig = this.workflowsStore.activeSystemConfig;

    if (
      !systemConfig ||
      systemConfig.search.length > 1 ||
      this.workflowsStore.activeSystem === "ALL"
    )
      return "ALL";
    return systemConfig.search[0]?.searchType;
  }

  get supervisorSearchIds(): string[] | undefined {
    const user = this.workflowsStore.user;
    if (!user) return undefined;

    const { info } = user;

    // return the current user's caseload and staff if current user
    // has at least one staff member they supervise upon login, otherwise
    // use the list updated in `selectedSearchIdsForSupervisorsWithStaff`
    if (this.hasSupervisedStaffAndRequiredFeatureVariant) {
      if (this.selectedSearchIdsForSupervisorsWithStaff) {
        return this.selectedSearchIdsForSupervisorsWithStaff;
      }
      const supervisedStaffIds =
        this.workflowsStore.staffSupervisedByCurrentUser.map(
          (staff) => staff.staffExternalId,
        );

      const currentUserId = info.hasCaseload ? [info.staffExternalId] : [];
      const staffAndCurrentUserIds = [...currentUserId, ...supervisedStaffIds];
      return staffAndCurrentUserIds;
    }
  }

  get selectedSearchIds(): string[] {
    const user = this.workflowsStore.user;
    if (!user) return [];

    if (this.supervisorSearchIds) return this.supervisorSearchIds;

    if (this.workflowsStore.rootStore.isImpersonating) {
      return this.selectedSearchIdsForImpersonation ?? [];
    }

    // A record with no stateCode at all is left alone — it predates the field
    // and there's nothing to place it in the wrong tenant.
    const stateCodeForUpdates = user.updates?.stateCode;
    if (
      stateCodeForUpdates &&
      stateCodeForUpdates !== this.workflowsStore.rootStore.currentTenantId
    ) {
      return [];
    }

    return user.updates?.selectedSearchIds ?? [];
  }

  // Returns the local selected-search overrides to the state they'd be in on a
  // fresh load into the new tenant, then clears the persisted selection.
  clearSelectedSearchForTenantChange(): void {
    this.updateSelectedSearch([]);
    this.selectedSearchIdsForSupervisorsWithStaff = undefined;
    this.selectedSearchIdsForImpersonation = undefined;
  }

  // TODO(OBT-49983): Remove once the Typesense-backed search refactor ships.
  // The ids of every facility unit available to search in the current
  // tenant — used to cap unit selections specifically, regardless of what
  // else (officers, districts) is selected alongside them. Keying off the
  // individual ids rather than `searchType` matters because `searchType` is
  // "ALL" (not "FACILITY_UNIT") on pages like the Workflows homepage that
  // let a user search across systems at once.
  get facilityUnitSearchIds(): Set<string> {
    return new Set(
      this.workflowsStore.availableLocations
        .filter(
          (location) =>
            location.idType === locationIdsBySearchType.FACILITY_UNIT,
        )
        .map((location) => location.locationId),
    );
  }

  updateSelectedSearch(searchIds: string[]): void {
    const user = this.workflowsStore.user;
    if (!user || !this.workflowsStore.rootStore.currentTenantId) return;

    // Enforced here too (not just in the UI) so the cap holds regardless of
    // caller — impersonation, defaults, or any future write path.
    const cappedSearchIds = this.capFacilityUnitSelections(searchIds);

    this.workflowsStore.rootStore.firestoreStore.updateSelectedSearchIds(
      cappedSearchIds,
    );

    // update the `selectedSearchIdsForSupervisorsWithStaff` for users with staff they supervise
    if (this.hasSupervisedStaffAndRequiredFeatureVariant) {
      this.selectedSearchIdsForSupervisorsWithStaff = cappedSearchIds;
    }

    this.selectedSearchIdsForImpersonation = cappedSearchIds;
  }

  // Keeps at most the INCARCERATION system's `maxFacilityUnitSearchIds` (see
  // `WorkflowsSystemConfig`) facility-unit ids in `searchIds`, leaving any
  // non-facility-unit entries (officers, districts) untouched. States that
  // don't set `maxFacilityUnitSearchIds` are unaffected, this is a no-op
  // for them.
  private capFacilityUnitSelections(searchIds: string[]): string[] {
    const maxFacilityUnitSearchIds =
      this.workflowsStore.systemConfigFor(
        "INCARCERATION",
      ).maxFacilityUnitSearchIds;
    if (maxFacilityUnitSearchIds === undefined) return searchIds;

    const facilityUnitSearchIds = this.facilityUnitSearchIds;
    let facilityUnitCount = 0;
    return searchIds.filter((id) => {
      if (!facilityUnitSearchIds.has(id)) return true;
      facilityUnitCount += 1;
      return facilityUnitCount <= maxFacilityUnitSearchIds;
    });
  }

  get selectedSearchables(): Searchable[] {
    // Under Typesense, availableSearchables only holds the current typeahead
    // page (per_page: 20), so a selected item can drop out of it as soon as
    // the input clears and a fresh query reseeds the results — which would
    // make its value pill vanish even though it's still selected. Resolve
    // against the manager's accumulating cache instead so pills persist.
    // The manager scopes that cache to the active system, matching the
    // system-scoping the Firestore path below gets from availableSearchables.
    const selected = this.isTypesenseSearchEnabled
      ? this.caseloadSearchManager.resolveSelectedSearchables(
          this.selectedSearchIds,
        )
      : this.availableSearchables
          .flatMap((searchableGroup) => searchableGroup.searchables)
          .filter((searchable) =>
            this.selectedSearchIds.includes(searchable.searchId),
          );

    return uniqBy(selected, (searchable) => searchable.searchId);
  }

  // The dropdown's source of truth. When the Typesense-backed search bar FV
  // is on, results come from caseloadSearchManager (server-populated via
  // typeahead + tenant/system-change seed). Otherwise the existing
  // Firestore-driven code path runs. The getter is pure — no side effects;
  // the fetch is triggered by reactions inside CaseloadSearchManager.
  get availableSearchables(): SearchableGroup[] {
    return this.isTypesenseSearchEnabled
      ? this.caseloadSearchManager.results
      : this.firestoreSearchResults;
  }

  get firestoreSearchResults(): SearchableGroup[] {
    switch (this.searchType) {
      case "FACILITY":
      case "FACILITY_UNIT":
      case "US_ID_CRC_FACILITY": {
        const idType = locationIdsBySearchType[this.searchType];
        return [
          {
            groupLabel: "All Facilities", // This is generic, but the groupLabel isn't shown with just one group
            searchables: this.workflowsStore.availableLocations
              .filter((l) => idType === l.idType)
              .map((location) => new Location(location)),
          },
        ];
      }
      case "DISTRICT": {
        return [
          {
            groupLabel: "All Districts",
            searchables: this.workflowsStore.availableLocations
              .filter((location) => location.idType === "districtId")
              .map((location) => new Location(location)),
          },
        ];
      }
      case "INCARCERATION_OFFICER":
      case "OFFICER": {
        if (this.hasSupervisedStaffAndRequiredFeatureVariant) {
          const staffWithCaseload =
            this.workflowsStore.staffSupervisedByCurrentUser.map(
              (officer) => new Officer(officer),
            );
          // include user's own caseload if they have one
          if (this.workflowsStore.user?.info.hasCaseload) {
            const currentUserStaffRecord =
              this.workflowsStore.availableOfficers.find(
                (officer) =>
                  officer.staffExternalId ===
                  this.workflowsStore.user?.info.staffExternalId,
              );
            if (currentUserStaffRecord) {
              staffWithCaseload.push(new Officer(currentUserStaffRecord));
            }
          }
          const staffWithCaseloadIdsSet = new Set(
            staffWithCaseload.map((officer) => officer.searchId),
          );
          const groupedOfficers = [
            {
              groupLabel: "Your Team",
              searchables: staffWithCaseload,
            },
            {
              groupLabel: "All Staff",
              searchables: this.workflowsStore.availableOfficers
                .filter(
                  (officer) =>
                    !staffWithCaseloadIdsSet.has(officer.staffExternalId) &&
                    officer.staffExternalId !==
                      this.workflowsStore.user?.info.staffExternalId,
                )
                .map((officer) => new Officer(officer)),
            },
          ];

          return groupedOfficers;
        }

        return [
          {
            groupLabel: "All Officers",
            searchables: this.workflowsStore.availableOfficers.map(
              (officer) => new Officer(officer),
            ),
          },
        ];
      }
      case "CASELOAD": {
        return [
          {
            groupLabel: "All Caseloads",
            searchables: this.workflowsStore.availableOfficers.map(
              (officer) => new CaseloadSearchable(officer),
            ),
          },
        ];
      }
      case "ALL": {
        const { availableLocations, availableOfficers } = this.workflowsStore;

        const districts = availableLocations
          .filter((location) => location.idType === "districtId")
          .map((district) => new Location(district));

        const facilities = availableLocations
          .filter((location) =>
            ["facilityId", "crcFacilityId", "facilityUnitId"].includes(
              location.idType,
            ),
          )
          .map((facility) => new Location(facility));

        const officers = availableOfficers.map(
          (officer) => new Officer(officer),
        );

        return [
          { groupLabel: "All Districts", searchables: districts },
          { groupLabel: "All Facilities", searchables: facilities },
          { groupLabel: "All Officers", searchables: officers },
        ].filter((group) => group.searchables.length);
      }
      case undefined:
        return [];
      default:
        assertNever(this.searchType);
    }
  }

  get hasSupervisedStaffAndRequiredFeatureVariant(): boolean {
    return Boolean(
      this.workflowsStore.featureVariants.workflowsSupervisorSearch &&
        this.workflowsStore.staffSupervisedByCurrentUser.length > 0,
    );
  }

  setSearchTypeOverride(override?: SearchType): void {
    this.searchTypeOverride = override;
  }

  // Deduped list of the searchTitles the tenant lets this user search by for
  // the currently active system: only SUPERVISION or INCARCERATION titles
  // when a single system is active; both, deduped, when `activeSystem` is
  // "ALL". Kept private — external consumers should read
  // `workflowsSearchFieldTitle` for the human-facing phrase.
  private get enabledSearchTitles(): string[] {
    const { activeSystem, workflowsSupportedSystems, systemConfigFor } =
      this.workflowsStore;
    if (!activeSystem || !workflowsSupportedSystems) return [];
    const supported: Exclude<SystemId, "ALL">[] = intersection(
      ["SUPERVISION", "INCARCERATION"] as Exclude<SystemId, "ALL">[],
      workflowsSupportedSystems as Exclude<SystemId, "ALL">[],
    );
    const systems: Exclude<SystemId, "ALL">[] =
      activeSystem === "ALL"
        ? supported
        : supported.filter((s) => s === activeSystem);
    return uniq(
      systems.flatMap((system) =>
        systemConfigFor(system).search.map((sc) => sc.searchTitle),
      ),
    );
  }

  /**
   * Natural-language phrase listing every searchable thing the tenant lets
   * this user search by, e.g. `"an officer"` / `"an officer or district"` /
   * `"an officer, district, or facility"`. Meant to slot into copy like:
   *
   *   Start typing the name of {workflowsSearchFieldTitle} above to…
   *
   * Returns `""` when the tenant has no configured search titles (edge case
   * — callers should fall back to a generic CTA).
   */
  get workflowsSearchFieldTitle(): string {
    const titles = this.enabledSearchTitles;
    if (titles.length === 0) return "";
    const article = /^[aeiou]/i.test(titles[0]) ? "an" : "a";
    if (titles.length === 1) return `${article} ${titles[0]}`;
    if (titles.length === 2) return `${article} ${titles[0]} or ${titles[1]}`;
    const [first, ...rest] = titles;
    const last = rest.pop();
    return `${article} ${first}, ${rest.join(", ")}, or ${last}`;
  }

  /**
   * Handle a click on a search pill, either selecting the pill if it isn't currently
   * selected or de-selecting it (and possibly resetting the active system) if it is.
   */
  handleSearchPillClick(
    searchType: SearchType,
    system: SystemId,
    allVisibleSystems: SystemId[],
  ): void {
    const { updateActiveSystem } = this.workflowsStore;

    // Updating the active system and searchTypeOverride narrows down the
    // availableSearchables to match the selected search pill.

    if (this.searchTypeOverride === searchType) {
      // Clicking on the currently selected pill de-selects it
      this.setSearchTypeOverride(undefined);

      // Do not clear the selected search ids

      // Update the active system to the user's only system if they can only see
      // one system on this page, or "ALL" otherwise
      const nextActiveSystem =
        allVisibleSystems.length === 1 ? allVisibleSystems[0] : "ALL";
      updateActiveSystem(nextActiveSystem);
    } else {
      // Clicking on a pill that is not currently selected:

      // selects the new pill
      this.setSearchTypeOverride(searchType);

      // clears the selected search ids since the search type changes
      this.updateSelectedSearch([]);

      // update the active system based on the selected pill
      updateActiveSystem(system);
    }
  }

  // Truthy when the Typesense-backed caseload search is on for this user.
  // Phased rollouts get their own FVs (typesenseCaseloadSearch is phase 1;
  // subsequent phases will add sibling FVs), so this getter doesn't need to
  // inspect a `variant` value — presence of the FV is enough. Read by both
  // this store's availableSearchables and by CaseloadSearchManager to gate
  // its own reactions.
  get isTypesenseSearchEnabled(): boolean {
    return Boolean(this.workflowsStore.featureVariants.typesenseCaseloadSearch);
  }

  // Truthy when the homepage reads its opportunities from Typesense (phase 2).
  //
  // Requires BOTH FVs. typesenseCaseloadSearch is what populates
  // selectedSearchIds from the Typesense search bar, so without it the two
  // paths disagree about who is selected. Read by both
  // WorkflowsHomepagePresenter and OpportunitySearchManager, which gates its
  // own reaction on it — defined once here so the two cannot drift apart.
  get isTypesenseHomepageEnabled(): boolean {
    return Boolean(
      this.workflowsStore.featureVariants.typesenseHomepage &&
        this.isTypesenseSearchEnabled,
    );
  }
}
